import { Ref } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormGetValues,
  UseFieldArrayReturn,
  FieldErrors,
} from 'react-hook-form';
import type { InitCompanyDto, IndustrySector, CompanyLocationDto } from '@monorepo/shared';
import { PrimaryButton } from '../ui/PrimaryButton';
import { AddressForm } from '../form/AddressForm';
import { CompanyInfoForm } from '../form/CompanyInfoForm';
import { ContactInfoForm } from '../form/ContactInfoForm';
import { DpoForm } from '../form/DpoForm';
import { FacilitiesForm } from '../form/FacilitiesForm';
import { MainCompanyLocationForm } from '../form/MainCompanyLocationForm';
import { LocationsManagement } from '../form/LocationsManagement';
import {
  PendingCompanyLogoSection,
  type PendingCompanyLogoSectionRef,
} from './PendingCompanyLogoSection';

interface CompanySettingsFormViewProps {
  register: UseFormRegister<InitCompanyDto>;
  errors: FieldErrors<InitCompanyDto>;
  setValue: UseFormSetValue<InitCompanyDto>;
  getValues: UseFormGetValues<InitCompanyDto>;
  locationsFieldArray: UseFieldArrayReturn<InitCompanyDto, 'locations'>;
  logoRef: Ref<PendingCompanyLogoSectionRef>;
  isMainContact: boolean;
  hasDpoDeclaration: boolean;
  hasDataProtectionRepresentative: boolean;
  isExternalConsultant: boolean;
  siren?: string;
  industrySector?: IndustrySector;
  errorMessage: string;
  isSubmitting: boolean;
  onSubmit: () => void;
}

export const CompanySettingsFormView = ({
  register,
  errors,
  setValue,
  getValues,
  locationsFieldArray,
  logoRef,
  isMainContact,
  hasDpoDeclaration,
  hasDataProtectionRepresentative,
  isExternalConsultant,
  siren,
  industrySector,
  errorMessage,
  isSubmitting,
  onSubmit,
}: CompanySettingsFormViewProps) => {
  const { t } = useTranslation();

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <CompanyInfoForm<InitCompanyDto>
        register={register}
        errors={errors}
        industrySector={industrySector}
      />

      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-text border-b pb-2">
          {t('company.section.head_office_address')}
        </h2>
        <AddressForm
          prefix="headOfficeAddress"
          register={register}
          errors={errors.headOfficeAddress || {}}
          showOptionalInfo={true}
        />
      </div>

      <ContactInfoForm register={register} errors={errors} isMainContact={isMainContact} />

      <DpoForm<InitCompanyDto>
        register={register}
        errors={errors.dataProtectionSettings || {}}
        hasDpoDeclaration={hasDpoDeclaration}
        hasDataProtectionRepresentative={hasDataProtectionRepresentative}
        isExternalConsultant={isExternalConsultant}
      />

      <FacilitiesForm<InitCompanyDto> register={register} />

      <MainCompanyLocationForm
        register={register}
        errors={
          (errors as FieldErrors<InitCompanyDto & { mainLocation: CompanyLocationDto }>)
            .mainLocation || {}
        }
        siren={siren}
      />

      <LocationsManagement<InitCompanyDto>
        register={register}
        setValue={setValue}
        getValues={getValues}
        errors={{ locations: errors.locations }}
        fieldArray={locationsFieldArray}
        siren={siren}
      />

      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-text border-b pb-2">
          {t('company.logo.section_title')}
        </h2>
        <PendingCompanyLogoSection ref={logoRef} />
      </div>

      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{errorMessage}</p>
        </div>
      )}

      <div className="flex justify-end pt-4">
        <PrimaryButton type="submit" loading={isSubmitting}>
          {t('company.cta.submit')}
        </PrimaryButton>
      </div>
    </form>
  );
};
